import { coinflipStore } from '@/stores/coinflip.store';
import { modalStore } from '@/stores/modal.store';
import { getLevelColor } from '@/utils/colors';
import { itemsValue } from '@/utils/itemInfo';
import { ICoinflipGame } from '@/utils/types/ICoinflipGame';
import AbstractAvatar from '@/components/Avatars/AbstractAvatar';
import LevelBadge from '@/components/Levels/LevelBadge';
import CoinflipPageHeader from './CoinflipPageHeader';
import CoinflipRow from './CoinflipRow';

const SectionTitle = ({
    label,
    note,
    className = '',
}: {
    label: string;
    note?: string;
    className?: string;
}) => (
    <div className={`flex items-center gap-2.5 text-[15px] font-bold tracking-[0.02em] ${className}`}>
        <span
            className="w-1 h-[18px] rounded-sm shrink-0"
            style={{ background: 'linear-gradient(#8b6dff,#3ad6ff)', boxShadow: '0 0 12px #8b6dff' }}
        />
        {label}
        {note && <span className="text-text_3 font-medium text-[13px]">· {note}</span>}
    </div>
);

// One winner from a finished coinflip — square avatar, gold winner ring, prize.
// Clicking opens the View-coinflip modal for that game.
const WinnerChip = ({ game, index = 0 }: { game: ICoinflipGame; index?: number }) => {
    const { setTitle, setComponent, setData, toggleModal } = modalStore((state) => state);
    const winner = game.winnerSide === game.firstSide ? game.firstUser : game.secondUser;
    const value = itemsValue([...game.firstItems, ...game.secondItems]);
    const lvl = Math.max(0, Math.floor((winner?.experience ?? 0) / 100));
    const lvlColor = getLevelColor(lvl);

    function open() {
        setTitle(`COINFLIP #${game.id}`);
        setComponent('View coinflip');
        setData({ game });
        toggleModal(true);
    }

    if (!winner) return null;

    return (
        <button
            onClick={open}
            title={`${winner.name} · $${value.toFixed(2)}`}
            className="group shrink-0 flex flex-col items-center gap-2 w-[68px]"
            style={{
                animation: 'cfWinIn 0.5s cubic-bezier(0.34,1.56,0.64,1) both',
                animationDelay: `${Math.min(index, 12) * 35}ms`,
            }}
        >
            <div className="relative w-14 h-14 transition-transform group-hover:-translate-y-1">
                <AbstractAvatar
                    seed={winner.name || String(winner.id)}
                    src={winner.avatar}
                    size={56}
                    square
                    className="!w-full !h-full"
                />
                <span
                    className="absolute -inset-1 rounded-[30%] pointer-events-none transition-all group-hover:-inset-[5px]"
                    style={{
                        border: `2px solid ${lvlColor}`,
                        boxShadow: `0 0 16px -3px ${lvlColor}`,
                    }}
                />
                <span
                    className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[13px] leading-none drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
                    aria-hidden
                >
                    👑
                </span>
                <div className="absolute -right-1.5 -bottom-1.5">
                    <LevelBadge level={lvl} color={lvlColor} size="sm" variant="solid" />
                </div>
            </div>
            <span
                className="font-chat-nick text-[11px] font-extrabold tabular-nums text-accent_5 group-hover:brightness-110"
                style={{ textShadow: '0 0 10px rgba(255,194,77,0.45)' }}
            >
                ${value.toFixed(2)}
            </span>
        </button>
    );
};

const CoinflipPage = () => {
    const { activeGames, historyGames, historyLoaded } = coinflipStore((state) => state);

    const active = activeGames().filter(Boolean);
    const history = historyGames().filter(Boolean).slice(0, 20);

    return (
        <div className="flex flex-col gap-3 min-w-0 animate-fluent-fadeup">
            <CoinflipPageHeader />

            {/* History — recent winners; click a winner to view that game */}
            <section className="surface px-4 py-3.5 flex flex-col gap-3">
                <SectionTitle
                    label="History"
                    note={historyLoaded ? `${history.length} recent winners` : 'loading…'}
                />
                {!historyLoaded ? (
                    <div className="flex gap-3 pb-1 pt-2 px-0.5">
                        {Array.from({ length: 9 }).map((_, i) => (
                            <div
                                key={i}
                                className="shrink-0 flex flex-col items-center gap-2 w-[68px]"
                            >
                                <div
                                    className="w-14 h-14 rounded-[28%] bg-layer_2 border border-stroke animate-pulse"
                                    style={{ animationDelay: `${i * 90}ms` }}
                                />
                                <div
                                    className="h-3 w-10 rounded bg-layer_2 animate-pulse"
                                    style={{ animationDelay: `${i * 90}ms` }}
                                />
                            </div>
                        ))}
                    </div>
                ) : history.length === 0 ? (
                    <div className="text-text_3 text-sm py-3 text-center">No finished games yet</div>
                ) : (
                    // generous top/side padding so the crown, glow and hover ring aren't clipped by overflow-x-auto
                    <div className="flex gap-3 overflow-x-auto fluent-scrollbar pt-6 pb-3 px-3">
                        {history.map((g, i) => (
                            <WinnerChip key={g.id} game={g} index={i} />
                        ))}
                    </div>
                )}
            </section>

            <section className="surface px-4 py-3.5 flex flex-col gap-3">
                <SectionTitle label="Active games" note={`${active.length} live`} />
                <div className="flex flex-col gap-2.5">
                    {active.length === 0 ? (
                        <div className="text-center py-8 text-text_3 text-sm">No active games</div>
                    ) : (
                        active.map((g) => <CoinflipRow key={g.id} game={g} />)
                    )}
                </div>
            </section>
        </div>
    );
};

export default CoinflipPage;
